import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import {useSelector, useDispatch} from 'react-redux';
import {createStyles, makeStyles, Theme, useTheme} from "@material-ui/core/styles";
import tagTextures, { defaultTagTexture, getTagTexturePath } from "../viewer/textures/tag-icons";
import Pagination from "@material-ui/lab/Pagination/Pagination";
import LinkIcon from '@material-ui/icons/Link';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from '@material-ui/icons/Edit';
import {
  createTags,
  updateTag,
  deleteTag,
  getTagAssets,
  selectAllAssets,
  TagData,
  TagItemData,
  Asset,
  createAsset,
  AssetType
} from "../redux/assets-slice";
import {createFolder, selectAllFolders} from '../redux/folders-slice';
import {
  DialogContent,
  InputLabel,
  TextField,
  Avatar,
  Grid,
  Collapse,
} from "@material-ui/core";
import clsx from 'clsx';
import {
  DraggableDialog,
  PromptDialog,
  EnhancedTable,
  SimpleDialog,
  InputWithLabel,
  DropdownWithLabel,
  DenseDivider,
  TextWithLabel,
  DividerWithText
} from "../components";
import { toast } from "../app";
import { getLocalizedFolderName } from "../utilities";
import LocalScene from "../viewer/js/projections";
import { useTranslation } from "react-i18next";
import { isStaticSite } from "../electron-modules";
import { nanoid } from "@reduxjs/toolkit";
import { useAuth, useDialog, useUniqueProjectID, useViewer } from "../hooks";
import { TagSize } from "../types/tags";
import { sendCustomEvent } from "../events";
import { useTableState } from "../hooks/use-table-state";
import { useAppDispatch } from "../redux/store";

const copyTextToClipboard = require('copy-to-clipboard');

export const tagIconsPerRow = 8;
export const tagRowsPerPage = 3;

const NEW_CSV_ID = "__new_csv__";
const NEW_FOLDER_ID = "__new_folder__";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      marginBottom: theme.spacing(3)
    },
    selectDivider: {
      margin: theme.spacing(0.5),
      opacity: 0.2
    },
    asterisk: {
      "&:after": {
        content:'"*"',
        color: "red",
        paddingLeft: theme.spacing(0.5)
      }
    },
    avatarParent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      paddingTop: theme.spacing(2)
    },
    tagAvatar: {
      width: "24px",
      height: "24px",
      cursor: "pointer",
      "&:hover": {
        width: "26px",
        height: "26px",
        margin: "-1px"
      }
    },
    tagIconPagination: {
      paddingBottom: theme.spacing(2)
    },
    tagIconSearchParent: {
      textAlign: "right",
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(1)
    },
    tagIconPageParent: {
      width: "100%",
      display: "flex",
      flexDirection: "row-reverse"
    },
    tagIconParent: {
      position: "relative",
      display: "inline-block",
      width: `calc(${100/tagIconsPerRow}% - ${theme.spacing(2)}px)`,
      padding: theme.spacing(1)
    },
    tagIconCheck: {
      userSelect: "none",
      bottom: theme.spacing(1),
      right: theme.spacing(1),
      position: "absolute",
      color: "green",
      backgroundColor: "white",
      borderRadius: "50%"
    },
    tagIconImage: {
      width: `calc(100% - ${theme.spacing(1)}px)`,
      height: `calc(100% - ${theme.spacing(1)}px)`,
      margin: theme.spacing(0.5),
      cursor: "pointer",
      "&:hover": {
        width: "100%",
        height: "100%",
        margin: "0px",
      }
    },
    content: {
      padding: theme.spacing(0),
      paddingTop: "0px !important"
    }
  }),
);

interface TagIconDialogProps {
  open: boolean
  onSubmit(texture: string, size: number): void
  onClose(): void
  currentTexture: string
  currentSize: number
}

export const TagIconDialog = memo((props: TagIconDialogProps) => {
  const {open, onSubmit, onClose, currentTexture, currentSize} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [tagTexture, setTagTexture] = useState("");
  const [tagSize, setTagSize] = useState(0);

  const handleIconClick = (texture) => {
    setTagTexture(texture);
  };

  const onSearch = (event) => {
    setPage(1);
    setSearch(event.target.value as string);
  };

  const onPageChange = (event, page: number) => {
    setPage(page);
  };

  const resetDefaults = () => {
    let selectedPage = Math.floor(
      textures.indexOf(currentTexture) / tagsPerPage) + 1;

    setSearch("");
    setTagTexture(currentTexture);
    setTagSize(currentSize);
    setPage(selectedPage);
  };

  useEffect(() => {
    if (!open) return;
    resetDefaults();
  }, [open]);

  const textures = useMemo(() => {
    const searchText = search.toLowerCase().trim();
    const searchTerms = searchText.split(" ");

    if (searchText === "") {
      return Object.keys(tagTextures);
    }

    return Object.keys(tagTextures)
      .map(key => {
        const itemText = key.toLowerCase();
        const matches = searchTerms.filter(x => itemText.includes(x)).length;
        return {key, matches};
      })
      .filter(x => x.matches >= searchTerms.length)
      .map(x => x.key);
  }, [search]);

  const iconChanged = (tagTexture !== currentTexture);
  const sizeChanged = (tagSize !== currentSize);

  const tagsPerPage = tagIconsPerRow * tagRowsPerPage;
  const numPages = Math.ceil(textures.length / tagsPerPage);

  const texturesForPage = textures.slice(
    (page-1) * tagsPerPage,
    page * tagsPerPage
  );

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      title={t("tags.edit-change-icon")}
      canSubmit={iconChanged || sizeChanged}
      onSubmit={() => {
        onSubmit(tagTexture, tagSize);
      }}
      button={t("buttons.apply-changes")}
    >
      <div className={classes.tagIconSearchParent}>
        <TextField
          label={t("tags.edit-search-icon")}
          type="search"
          onChange={onSearch}
          value={search}
          size="small"
        />
      </div>

      <div>
        {texturesForPage.map(texture => (
          <div key={texture} className={classes.tagIconParent}>
            <img
              alt=""
              title={texture}
              className={classes.tagIconImage}
              src={getTagTexturePath(texture)}
              onClick={() => {
                handleIconClick(texture);
              }}
            />

            {(texture === tagTexture) && (<CheckCircleIcon
              fontSize="small"
              className={classes.tagIconCheck}
            />)}
          </div>
        ))}
      </div>

      <div className={classes.tagIconPageParent}>
        <Pagination
          page={page}
          count={numPages}
          onChange={onPageChange}
          className={classes.tagIconPagination}
        ></Pagination>
      </div>

      <DenseDivider/>

      <div style={{
        display: "flex",
        justifyContent: "center",
      }}>
        <Avatar
          src={getTagTexturePath(tagTexture)}
          variant="square"
          style={{
            margin: "auto 16px",
            zoom: 1 + (tagSize-1)/2.0,
          }}
        />

        {/** Icon Size */}
        <Grid item xs={6}>
          <DropdownWithLabel
            title={t('tags.icon_size')}
            label={t('tags.the_size_of_the_tag_icon')}
            value={tagSize}
            onChange={setTagSize}
            options={[
              [TagSize.XSmall, t('sizes.extra_small')],
              [TagSize.Small, t('sizes.small')],
              [TagSize.Medium, t('sizes.medium')],
              [TagSize.Large, t('sizes.large')],
              [TagSize.XLarge, t('sizes.extra_large')]
            ]}
          />
        </Grid>
      </div>

    </SimpleDialog>
  );
});

interface TagItemsDialogProps {
  asset: Asset
  open: boolean;
  title: string;
  onClose(): void;
}

export const TagItemsDialog = (props: TagItemsDialogProps) => {
  const {asset, open, title, onClose} = props;

  const {viewer} = useViewer();
  const dispatch = useDispatch();
  const {i18n, t} = useTranslation();

  const deleteDialog = useDialog();
  const {permissions} = useAuth();
  const tableState = useTableState({defaultSort: "name"});

  const formatter = viewer.getMeasurementFormatter();
  const precision = formatter.getVectorPrecision(LocalScene.viewProjection);

  const onEdit = (row) => {
    showTagDetails(row.tagID, true);
  };

  const onDelete = (row) => {
    dispatch(deleteTag(row.tagID));
  };

  const onRowClick = (row, index) => {
    if (index > 2) return;
    viewer.jumpToTag(row.tagID, true);
  };

  const getRows = (items) => {
    return items.map((tag, index) => {
      const isAerialTag = !("z" in tag);
      const x = tag.x.toFixed(precision.x);
      const y = tag.y.toFixed(precision.y);
      const z = isAerialTag ? "N/A" : tag.z.toFixed(precision.z);

      const coordinate = isAerialTag
        ? `(${x}, ${y})`
        : `(${x}, ${y}, ${z})`;

      const position = isAerialTag
        ? [tag.x, tag.y]
        : [tag.x, tag.y, tag.z];

      let rowData = {
        id: `TagTableRow_${tag.id}`,
        tagID: tag.id,
        tag: index + 1,
        modified: tag.date,
        name: tag.name,
        comment: tag.comment,
        coordinate,
        position
      };

      return rowData;
    });
  };

  const tagItems = (asset.data as TagData).items;

  const actions = useMemo(() => {
    const zoomToTag = {
      title: t('tags.move_to_tag'),
      icon: <ZoomInIcon fontSize="small"/>,
      onClick: (row) => {
        viewer.jumpToTag(row.tagID, true);
      }
    };

    const copyPosition = {
      title: t("tags.copy-to-clipboard"),
      icon: <FileCopyIcon fontSize="small"/>,
      onClick: (row) => {
        // Copy position to clipboard
        copyTextToClipboard(row.coordinate);
        toast.success(t("toast.copied-to-clipboard"));
      }
    };

    const copyURL = {
      title: t('tags.copy_tag_url'),
      icon: <LinkIcon fontSize="small"/>,
      onClick: (row) => {
        // Copy url to clipboard
        const url = new URL(window.location as any);
        url.hash = `tag=${row.tagID}`;
        copyTextToClipboard(url.href);
        toast.success(t("toast.copied-to-clipboard"));
      }
    };

    const deleteRow = {
      title: t("buttons.delete"),
      icon: <DeleteIcon fontSize="small"/>,
      onClick: (row) => {
        deleteDialog.handleOpen(row);
      }
    };

    const editRow = {
      title: t("buttons.edit"),
      icon: <EditIcon fontSize="small"/>,
      onClick: onEdit
    };

    let actions = [];
    actions.push(zoomToTag);
    actions.push(copyPosition);

    if (isStaticSite) {
      actions.push(copyURL);
    }

    if (permissions.edit) {
      actions.push(editRow);
    }

    if (permissions.delete) {
      actions.push(deleteRow);
    }

    return actions;
  }, [i18n.language, permissions, isStaticSite]);

  const columns = [
    {
      id: 'name',
      label: t("tags.table-name"),
      wordBreak: true,
      searchable: true
    },
    {
      id: 'comment',
      label: t("tags.table-comments"),
      wordBreak: true,
      searchable: true
    },
    {
      id: 'modified',
      label: t("tags.table-date"),
      date: true
    },
  ];

  const rows = useMemo(() => {
    return getRows(tagItems);
  }, [tagItems]);

  let deletePrompt = "";
  if (deleteDialog.open) {
    deletePrompt = t('tags.do_you_want_to_delete_this_tag_row_name', {
      name: deleteDialog.data.name
    });
  }

  return (
    <React.Fragment>
      <DraggableDialog
        open={open}
        title={title}
        onClose={onClose}
        initialWidth={"fit-content"}
      >
        <DialogContent>
          <EnhancedTable
            state={tableState}
            columns={columns}
            rows={rows}
            actions={actions}
            onClick={onRowClick}
          />
        </DialogContent>
      </DraggableDialog>

      {/* Delete Dialog */}
      <PromptDialog
        open={deleteDialog.open}
        onCancel={deleteDialog.handleClose}
        onSubmit={() => {
          onDelete(deleteDialog.data);
          deleteDialog.handleClose();
        }}
        title={t("enhanced-table.prompt-delete-title")}
        prompt={deletePrompt}
        button={t('buttons.delete')}
      />
    </React.Fragment>
  );
};

interface TagDeleteDialogProps {
  open: boolean;
  onClose(): void;
  toDelete: string[];
}

export const TagDeleteDialog = (props: TagDeleteDialogProps) => {
  const {open, onClose, toDelete = []} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const onSubmit = () => {
    toDelete.forEach(tagID => {
      dispatch(deleteTag(tagID));
    });

    onClose();
    toast.success(t("toast.tag-removed", {
      count: numTagsToDelete
    }));
  };

  const numTagsToDelete = toDelete.length;

  return (
    <PromptDialog
      open={open}
      onCancel={onClose}
      onSubmit={onSubmit}
      title={t("dialog.delete-tags")}
      prompt={t("toast.tag-remove-prompt", {
        count: numTagsToDelete
      })}
      button={t("buttons.delete")}
    />
  );
};

export const TagDetailsDialog = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const assets = useSelector(selectAllAssets);
  const tagAssets = getTagAssets(assets);
  const uniqueProjectID = useUniqueProjectID();
  const tagDetailsDialog = useDialog();
  const {permissions} = useAuth();

  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [tagToEdit, setTagToEdit] = useState<string>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    /** Close dialog when new project is loaded */
    onClose();
  }, [uniqueProjectID]);

  /** Tag details event listener */
  useEffect(() => {
    const callback = (event) => {
      const {tagID, editing} = event.detail;
      setTagToEdit(tagID);
      setEditing(editing);
    };

    document.addEventListener("tag-details", callback);

    return () => {
      document.removeEventListener("tag-details", callback);
    };
  }, []);

  useEffect(() => {
    const allTags = tagAssets.map(asset => (asset.data as TagData).items).flat();
    const tag = allTags.find(x => x.id === tagToEdit);
    if (!tag) return;

    setTitle(tag.name);
    setComment(tag.comment);
    tagDetailsDialog.handleOpen();
  }, [tagToEdit]);

  const onClose = () => {
    setTagToEdit(null);
    tagDetailsDialog.handleClose();
  };

  const onEdit = () => {
    setEditing(true);
  };

  const onSubmit = () => {

    dispatch(updateTag({
      tagID: tagToEdit,
      name: title,
      comment: comment
    }));

    onClose();
    toast.success(t("toast.tag-details-update"));
  };

  const titleMissing = title.trim() === "";
  const canSubmit = !titleMissing;
  const canEdit = permissions.edit && !editing;

  return (
    <SimpleDialog
      open={tagDetailsDialog.open}
      onClose={onClose}
      title={t("dialog.tag-details")}
      maxWidth={"xs"}
      canSubmit={canSubmit}
      onEdit={canEdit ? onEdit : null}
      onSubmit={editing ? onSubmit : null}
      button={t("buttons.save-changes")}
    >
      <Grid container spacing={2}>

        {editing && <React.Fragment>
          {/** Title */}
          <Grid item xs={12}>
            <InputWithLabel
              title={t("tags.tag-title")}
              value={title}
              onChange={setTitle}
            />
          </Grid>

          {/** Comments */}
          <Grid item xs={12}>
            <InputWithLabel
              multiline
              required={false}
              title={t("tags.tag-comments")}
              value={comment}
              onChange={setComment}
            />
          </Grid>
        </React.Fragment>}

        {!editing && <React.Fragment>
          {/** Title */}
          <Grid item xs={12}>
            <TextWithLabel
              title={t("tags.tag-title")}
              value={title}
            />
          </Grid>

          {/** Comments */}
          <Grid item xs={12}>
            <TextWithLabel
              title={t("tags.tag-comments")}
              value={comment}
            />
          </Grid>
        </React.Fragment>}

      </Grid>
    </SimpleDialog>
  );
};

interface TagCreateDialogProps {
  open: boolean;
  onClose(): void;
  coordinate: number[];
}

export const TagCreateDialog = memo((props: TagCreateDialogProps) => {
  const {open, onClose, coordinate} = props;

  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const iconUpdateDialog = useDialog();
  const {viewer} = useViewer();

  const assets = useSelector(selectAllAssets);
  const tagAssets = getTagAssets(assets);
  const folders = useSelector(selectAllFolders);

  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [collectionID, setCollectionID] = useState("");
  const [collectionName, setCollectionName] = useState("");
  const [folderID, setFolderID] = useState(NEW_FOLDER_ID);
  const [tagTexture, setTagTexture] = useState(defaultTagTexture);
  const [tagSize, setTagSize] = useState(TagSize.Medium);

  const onSubmit = async () => {
    onClose();

    const newTag = {
      id: nanoid(),
      name: title,
      comment: comment,
      date: new Date().toISOString(),
      x: coordinate[0],
      y: coordinate[1],
    } as TagItemData;

    if (coordinate.length === 3) {
      newTag.z = coordinate[2];
      newTag.sceneState = viewer.sceneState;
    }

    let tagFolderID = folderID;
    let tagCollectionID = collectionID;

    if (createNewFolder) {
      // Create new folder
      tagFolderID = await dispatch(createFolder());
    }

    if (createNewFile) {
      // Create new tag collection
      tagCollectionID = await dispatch(createAsset({
        folderID: tagFolderID,
        name: collectionName,
        type: AssetType.Tag,
        texture: tagTexture,
        size: tagSize
      }));
    }

    // Add new tag to collection
    await dispatch(createTags({
      collectionID: tagCollectionID,
      tags: [newTag]
    }));

    toast.success(t("toast.tag-added"));
  };

  const resetDefaultValues = () => {
    setTitle("");
    setComment("");
    setCollectionName("");
    setTagTexture(defaultTagTexture);

    iconUpdateDialog.handleClose();

    if (hasExistingFolders) {
      setFolderID(folders[0].id);
    } else {
      setFolderID(NEW_FOLDER_ID);
    }

    if (hasExistingCSVs) {
      setCollectionID(tagAssets[0].id);
    } else {
      setCollectionID("");
    }
  };

  const submitTagIcon = useCallback((texture: string, size: number) => {
    setTagTexture(texture);
    setTagSize(size);
    iconUpdateDialog.handleClose();
  }, []);

  useEffect(() => {
    if (!open) return;
    resetDefaultValues();
  }, [open]);

  const hasExistingCSVs = tagAssets.length > 0;
  const hasExistingFolders = folders.length > 0;
  const createNewFile = collectionID === NEW_CSV_ID;
  const createNewFolder = folderID === NEW_FOLDER_ID;

  /* Determine valid state for new tag */
  let canSubmit = true;
  const titleMissing = title.trim() === "";
  const nameMissing = collectionName.trim() === "";
  canSubmit = canSubmit && !titleMissing;
  canSubmit = canSubmit && !(nameMissing && createNewFile);

  // Folder is used if it is user made or if it contains assets
  const folderOptions = folders.filter(folder => {
    const items = assets.filter(x => x.folderID === folder.id);
    return folder.default ? items.length > 0 : true;
  });

  return (
    <React.Fragment>
      {/* New tag dialog */}
      <SimpleDialog
        open={open && !iconUpdateDialog.open}
        onClose={onClose}
        onSubmit={onSubmit}
        canSubmit={canSubmit}
        maxWidth={"xs"}
        title={t("tags.new-title")}
        button={t('buttons.submit')}
      >
        <Grid container spacing={2}>

          {/** Title */}
          <Grid item xs={12}>
            <InputWithLabel
              title={t("tags.tag-title")}
              value={title}
              onChange={setTitle}
            />
          </Grid>

          {/** Comments */}
          <Grid item xs={12}>
            <InputWithLabel
              multiline
              required={false}
              title={t("tags.tag-comments")}
              value={comment}
              onChange={setComment}
            />
          </Grid>

          <Grid item xs={12}>
            <DropdownWithLabel
              title={t('tags.tag_collection')}
              value={collectionID}
              onChange={setCollectionID}
              seperator={[0]}
              options={[
                [NEW_CSV_ID, t('tags.create_new_collection')],
                ...tagAssets.map(tagCSV => [tagCSV.id, tagCSV.name])
              ] as any}
            />
          </Grid>

          <div style={{
            width: "100%",
            padding: theme.spacing(0, 1)
          }}>
            <Collapse in={createNewFile}>
              <DividerWithText
                label={t('tags.new_collection_details')}
              />

              {/** Tag collection name */}
              <Grid item xs={12}>
                <InputWithLabel
                  title={t('tags.collection_name')}
                  value={collectionName}
                  onChange={setCollectionName}
                />
              </Grid>

              {/** Tag collection folder */}
              <Grid item xs={12}>
                <DropdownWithLabel
                  title={t('tags.collection_folder')}
                  value={folderID}
                  onChange={setFolderID}
                  seperator={[0]}
                  options={[
                    [NEW_FOLDER_ID, t("tags.new-folder")],
                    ...folderOptions.map(folder => {
                      const localizedFolderName = getLocalizedFolderName(folder.name);
                      return [folder.id, localizedFolderName];
                    })
                  ]}
                />
              </Grid>

              {/** Default Icon */}
              <Grid item xs={12}>
                <div className={clsx(classes.avatarParent, classes.textField)}>
                  <InputLabel>
                    {t('tags.collection_icon')}
                  </InputLabel>
                  <Avatar
                    src={getTagTexturePath(tagTexture)}
                    onClick={iconUpdateDialog.handleOpen}
                    className={classes.tagAvatar}
                  />
                </div>
              </Grid>
            </Collapse>
          </div>

        </Grid>
      </SimpleDialog>

      {/* Tag Icon */}
      <TagIconDialog
        open={iconUpdateDialog.open}
        currentTexture={tagTexture}
        currentSize={tagSize}
        onSubmit={submitTagIcon}
        onClose={iconUpdateDialog.handleClose}
      />
    </React.Fragment>
  );
});

export const showTagDetails = (tagID: string, editing: boolean) => {
  sendCustomEvent("tag-details", {tagID, editing});
};
